import FeedbackPanel from './FeedbackPanel';
import { DashboardMobileNav } from './MobileNav';
import AdminReviewsPanel from './AdminReviewsPanel';
import AdminLiveReviews from './AdminLiveReviews';
import AdminLiveUsers from './AdminLiveUsers';
import ClipGallery from './ClipGallery';
import ReferralPanel from './ReferralPanel';
import UsageAnalytics from './UsageAnalytics';
import AdminUsersPanel from './AdminUsersPanel';
import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '../contexts/UserContext';
import './UserDashboard.css';

const UserDashboard = ({ onNavigateToApp }) => {
  const { user, logout } = useUser();
  const [mobileTab, setMobileTab] = useState('overview');
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [aiInsights, setAiInsights] = useState(null);
  const [systemStatus, setSystemStatus] = useState({ cpu: 0, memory: 0, queue: 0 });
  const [cookieHealth, setCookieHealth] = useState(null);
  const [trendingContent, setTrendingContent] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [presenceStatus, setPresenceStatus] = useState('online');
  const [notifications, setNotifications] = useState([]);
  const [inboxMessages, setInboxMessages] = useState([]);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showEmailSupport, setShowEmailSupport] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [paymentPlan, setPaymentPlan] = useState('');
  const [processingStatus, setProcessingStatus] = useState(null);
  const [esSubject, setEsSubject] = useState('');
  const [esMessage, setEsMessage] = useState('');
  const [esLoading, setEsLoading] = useState(false);
  const [esMsg, setEsMsg] = useState('');
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwMsg, setPwMsg] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  const handleChangePassword = async () => {
    if (pwNew !== pwConfirm) { setPwMsg('Passwords do not match'); return; }
    if (pwNew.length < 6) { setPwMsg('Password must be at least 6 characters'); return; }
    setPwLoading(true); setPwMsg('');
    try {
      const r = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + localStorage.getItem('auth_token') },
        body: JSON.stringify({ currentPassword: pwCurrent, newPassword: pwNew })
      });
      const d = await r.json();
      if (r.ok) { setPwMsg('✅ ' + d.message); setPwCurrent(''); setPwNew(''); setPwConfirm(''); }
      else setPwMsg('❌ ' + (d.error || 'Failed'));
    } catch(e) { setPwMsg('❌ Network error'); }
    setPwLoading(false);
  };
  // Removed inbox functionality - using direct email replies
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isFirstTimeLogin, setIsFirstTimeLogin] = useState(false);

  // Move getPlanDisplayName function to top to avoid hoisting issues
  const getPlanDisplayName = (plan) => {
    if (plan === 'free') return 'Free Tier';
    if (plan === 'pro') return 'Creator Pro';
    if (plan === 'business') return 'Business Plan';
    return plan;
  };

  // Detect payment success/cancelled from Stripe redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    if (payment === 'success') {
      setPaymentPlan(user.plan === 'business' ? 'Business' : 'Creator Pro');
      setShowPaymentSuccess(true);
      // Clean URL
      window.history.replaceState({}, '', '/dashboard');
    } else if (payment === 'cancelled') {
      window.history.replaceState({}, '', '/dashboard');
    }
  }, [user.plan]);

  useEffect(() => {
    fetchUserAnalytics();
    fetchAiInsights();
    fetchSystemStatus();
    fetchTrendingContent();
    fetchRecentActivity();
    fetchNotifications();
    // Fetch cookie health for admin
    if (['test@example.com','dwighth@hotmail.com'].includes(user.email?.toLowerCase())) {
      fetch('/api/admin/cookie-health').then(r => r.json()).then(d => setCookieHealth(d)).catch(() => {});
    }
    
    // Re-fetch analytics when tab becomes visible (after processing completes)
    const handleVisibility = () => {
      if (!document.hidden) fetchUserAnalytics();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    // Also re-fetch analytics every 30 seconds while on dashboard
    const analyticsRefreshInterval = setInterval(fetchUserAnalytics, 30000);
    // Removed inbox fetching
    
    // Check if this is user's first time logging in
    const loginCount = localStorage.getItem('loginCount_' + (user.email || user.userId)) || '0';
    const currentCount = parseInt(loginCount) + 1;
    localStorage.setItem('loginCount_' + (user.email || user.userId), currentCount.toString());
    setIsFirstTimeLogin(currentCount === 1);
    
    // Set up real-time updates
    const statusInterval = setInterval(() => {
      fetchSystemStatus();
      // Removed periodic inbox refresh
    }, 30000); // Update every 30 seconds
    
    // Set up clock updates
    const clockInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000); // Update every second
    
    // Set up daily AI insights refresh
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    
    const timeUntilMidnight = tomorrow.getTime() - now.getTime();
    
    const aiInsightsRefreshTimeout = setTimeout(() => {
      // Refresh AI insights at midnight
      fetchAiInsights();
      
      // Set up daily interval for AI insights
      const aiDailyInterval = setInterval(() => {
        fetchAiInsights();
      }, 24 * 60 * 60 * 1000); // 24 hours
      
      return () => clearInterval(aiDailyInterval);
    }, timeUntilMidnight);

    return () => {
      clearInterval(statusInterval);
      clearInterval(clockInterval);
      clearInterval(analyticsRefreshInterval);
      clearTimeout(aiInsightsRefreshTimeout);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user.email, user.userId]);
  
  // Presence heartbeat - sends status every 30s
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    const getStatus = () => document.hidden ? 'away' : 'online';
    const sendHeartbeat = (status) => {
      fetch('/api/presence/heartbeat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify({ status: status || getStatus() })
      }).catch(() => {});
    };
    const updatePresence = () => {
      const s = getStatus();
      setPresenceStatus(s);
      sendHeartbeat(s);
    };
    updatePresence();
    const heartbeatInterval = setInterval(updatePresence, 30000);
    const onVisChange = () => { updatePresence(); };
    document.addEventListener('visibilitychange', onVisChange);
    return () => {
      clearInterval(heartbeatInterval);
      document.removeEventListener('visibilitychange', onVisChange);
      sendHeartbeat('offline');
    };
  }, []);

  const fetchUserAnalytics = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/user/analytics', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAiInsights = useCallback(async () => {
    // Simulate AI insights based on current trends and user data
    setTimeout(() => {
      const currentHour = new Date().getHours();
      const currentDay = new Date().getDay(); // 0 = Sunday, 1 = Monday, etc.
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      
      // Dynamic insights based on time and trends
      const timeBasedInsights = {
        morning: {
          optimization: "Morning content performs 31% better on LinkedIn and Twitter",
          recommendation: "Post educational content between 8-10 AM for professional audience",
          nextBestAction: "Create a productivity tip video for morning commuters"
        },
        afternoon: {
          optimization: "Afternoon posts see 45% higher engagement on Instagram and TikTok", 
          recommendation: "Share lifestyle and entertainment content 12-4 PM for peak visibility",
          nextBestAction: "Create a quick tutorial or behind-the-scenes content"
        },
        evening: {
          optimization: "Evening content gets 67% more shares across all platforms",
          recommendation: "Post engaging, shareable content 6-9 PM for maximum viral potential", 
          nextBestAction: "Create trending topic reaction or commentary video"
        },
        night: {
          optimization: "Late night content performs best on TikTok with younger audiences",
          recommendation: "Share creative, experimental content 9 PM-12 AM for Gen Z engagement",
          nextBestAction: "Create a trending challenge or entertainment video"
        }
      };

      let timeSlot = 'morning';
      if (currentHour >= 12 && currentHour < 17) timeSlot = 'afternoon';
      else if (currentHour >= 17 && currentHour < 21) timeSlot = 'evening'; 
      else if (currentHour >= 21 || currentHour < 6) timeSlot = 'night';

      const insights = timeBasedInsights[timeSlot];
      
      // Add day-specific recommendations
      const weekdayBonus = currentDay >= 1 && currentDay <= 5 ? 8 : 0;
      const trendingBonus = Math.random() > 0.5 ? 12 : 6;
      
      setAiInsights({
        optimization: insights.optimization,
        recommendation: `${insights.recommendation} (Best day: ${dayNames[currentDay]})`,
        viralPotential: Math.min(95, 72 + weekdayBonus + trendingBonus + Math.floor(Math.random() * 10)),
        contentGaps: [
          "AI Tools tutorials", 
          "Short-form educational content", 
          "Behind-the-scenes workflow",
          "Quick productivity hacks",
          "Tech trend commentary"
        ],
        nextBestAction: insights.nextBestAction,
        trendingTopics: [
          "AI productivity tools",
          "Remote work setups", 
          "Tech reviews 2024",
          "Content creation tips",
          "Social media trends"
        ],
        optimalDuration: currentHour < 12 ? "60-90 seconds" : "15-45 seconds",
        expectedReach: `${Math.floor(Math.random() * 500 + 200)}K - ${Math.floor(Math.random() * 800 + 600)}K views`
      });
    }, 1000);
  }, []);

  const fetchSystemStatus = useCallback(async () => {
    try {
      const res = await fetch('/api/system-status');
      if (res.ok) {
        const data = await res.json();
        setSystemStatus(data);
      } else {
        throw new Error('Failed to fetch');
      }
    } catch (error) {
      setSystemStatus({
        cpu: 0,
        memory: 0,
        queue: Math.floor(Math.random() * 12),
        uptime: "99.8%",
        region: "US-East"
      });
    }
  }, []);

  const fetchTrendingContent = useCallback(async () => {
    try {
      const today = new Date();
      const todayKey = today.toDateString();
      const userKey = user.email || user.userId;
      const cacheKey = `trending_content_${userKey}_${todayKey}`;
      
      // Check if we have fresh data for today
      const cachedData = localStorage.getItem(cacheKey);
      if (cachedData) {
        setTrendingContent(JSON.parse(cachedData));
        return;
      }
      
      // Try to fetch real trending data from our backend (which can aggregate from multiple sources)
      let trendingData = null;
      
      try {
        const response = await fetch('/api/trending-content', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const webData = await response.json();
          trendingData = webData.trends;
        }
      } catch (apiError) {
        console.log('API trending data not available, using enhanced algorithm:', apiError.message);
      }
      
      // Enhanced algorithm with web-like patterns and real-time trends simulation
      if (!trendingData) {
        const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
        const currentHour = today.getHours();
        const dayOfWeek = today.getDay();
        
        // Enhanced trending topics with more realistic variety and seasonal trends
        const seasonalMultiplier = getSeasonalMultiplier(today.getMonth());
        const weekdayMultiplier = dayOfWeek >= 1 && dayOfWeek <= 5 ? 1.2 : 1.0; // Weekday boost
        const hourlyMultiplier = getHourlyMultiplier(currentHour);
        
        const trendingTopics = [
          // AI & Tech trends (always popular in creator space)
          { topic: "AI Content Creation", baseEngagement: 920, category: "tech", platforms: ["YouTube", "Twitter", "LinkedIn"] },
          { topic: "ChatGPT Automation", baseEngagement: 850, category: "tech", platforms: ["YouTube", "Twitter", "TikTok"] },
          { topic: "Video Editing AI", baseEngagement: 780, category: "tech", platforms: ["YouTube", "TikTok", "Instagram"] },
          { topic: "Productivity Apps 2025", baseEngagement: 720, category: "tech", platforms: ["LinkedIn", "YouTube", "Twitter"] },
          
          // Creator economy trends
          { topic: "Creator Monetization", baseEngagement: 890, category: "content", platforms: ["YouTube", "Instagram", "TikTok"] },
          { topic: "Short-Form Content", baseEngagement: 810, category: "content", platforms: ["TikTok", "Instagram", "YouTube"] },
          { topic: "Viral Video Strategies", baseEngagement: 750, category: "content", platforms: ["TikTok", "YouTube", "Twitter"] },
          { topic: "Social Media Growth", baseEngagement: 690, category: "content", platforms: ["Instagram", "TikTok", "LinkedIn"] },
          
          // Work & business trends (especially relevant for creator tools)
          { topic: "Remote Work Tools", baseEngagement: 740, category: "work", platforms: ["LinkedIn", "YouTube", "Twitter"] },
          { topic: "Digital Nomad Life", baseEngagement: 680, category: "work", platforms: ["Instagram", "TikTok", "YouTube"] },
          { topic: "Online Business", baseEngagement: 650, category: "work", platforms: ["YouTube", "LinkedIn", "Instagram"] },
          { topic: "Side Hustle Ideas", baseEngagement: 620, category: "work", platforms: ["TikTok", "Instagram", "YouTube"] },
          
          // Entertainment & lifestyle (viral potential)
          { topic: "Lifestyle Optimization", baseEngagement: 700, category: "lifestyle", platforms: ["Instagram", "TikTok", "YouTube"] },
          { topic: "Personal Development", baseEngagement: 660, category: "lifestyle", platforms: ["LinkedIn", "YouTube", "Instagram"] },
          { topic: "Creative Challenges", baseEngagement: 640, category: "lifestyle", platforms: ["TikTok", "Instagram", "YouTube"] },
          { topic: "Wellness Trends", baseEngagement: 580, category: "lifestyle", platforms: ["Instagram", "YouTube", "TikTok"] }
        ];

        // Enhanced randomization for realistic daily trends
        const random = (seed) => {
          const x = Math.sin(seed) * 10000;
          return x - Math.floor(x);
        };

        // Select 4-6 trending topics with intelligent selection
        const topicsCount = 4 + Math.floor(random(dayOfYear) * 3); // 4-6 topics
        const shuffledTopics = [...trendingTopics]
          .sort(() => random(dayOfYear + userKey.length) - 0.5)
          .slice(0, topicsCount);

        trendingData = shuffledTopics.map((topic, index) => {
          // Realistic engagement calculation with multiple factors
          const baseDailyVar = 0.7 + (random(dayOfYear + index) * 0.6); // ±30% daily variation
          const seasonalEffect = seasonalMultiplier;
          const weekdayEffect = weekdayMultiplier;
          const hourlyEffect = hourlyMultiplier;
          const trendingBoost = 1.0 + (random(dayOfYear + index + 100) * 0.3); // Up to +30% trending boost
          
          const finalEngagement = Math.floor(
            topic.baseEngagement * baseDailyVar * seasonalEffect * weekdayEffect * hourlyEffect * trendingBoost
          );
          
          // Growth calculation based on category and current trends
          const categoryGrowthRates = {
            tech: { min: 25, max: 65 }, // Tech trends grow fast
            content: { min: 20, max: 55 }, // Creator content is very popular
            work: { min: 15, max: 45 }, // Business trends steady
            lifestyle: { min: 18, max: 50 } // Lifestyle can be very viral
          };
          
          const growthRange = categoryGrowthRates[topic.category];
          const baseGrowth = growthRange.min + (random(dayOfYear + index + 200) * (growthRange.max - growthRange.min));
          const growth = Math.floor(baseGrowth * weekdayEffect * hourlyEffect);

          return {
            topic: topic.topic,
            engagement: finalEngagement,
            growth: `+${growth}%`,
            category: topic.category,
            momentum: growth > 45 ? 'High' : growth > 30 ? 'Medium' : 'Growing',
            platforms: topic.platforms,
            lastUpdated: new Date().toISOString(),
            peakHours: getPeakHours(topic.category),
            viralPotential: Math.floor(70 + (growth * 0.4)) // 70-96% range
          };
        });
      }

      setTrendingContent(trendingData);
      
      // Cache the data for the day
      localStorage.setItem(cacheKey, JSON.stringify(trendingData));
      
    } catch (error) {
      console.error('Failed to fetch trending content:', error);
      
      // Enhanced fallback with platform and viral potential data
      const fallbackTopics = [
        { topic: "AI Video Creation", engagement: 850, growth: "+42%", momentum: "High", platforms: ["YouTube", "TikTok"], viralPotential: 87 },
        { topic: "Creator Tools 2025", engagement: 750, growth: "+38%", momentum: "High", platforms: ["Instagram", "YouTube"], viralPotential: 83 },
        { topic: "Short-Form Content", engagement: 680, growth: "+35%", momentum: "Medium", platforms: ["TikTok", "Instagram"], viralPotential: 79 },
        { topic: "Viral Strategies", engagement: 620, growth: "+31%", momentum: "Medium", platforms: ["TikTok", "Twitter"], viralPotential: 75 }
      ];
      
      setTrendingContent(fallbackTopics);
    }
  }, [user.email, user.userId]);
  
  // Helper functions for enhanced trending algorithm
  const getSeasonalMultiplier = (month) => {
    // Seasonal trends: Jan-Mar (New Year productivity), Apr-Jun (Spring growth), 
    // Jul-Sep (Summer content), Oct-Dec (Holiday season)
    const seasonalFactors = [1.2, 1.1, 1.0, 1.1, 1.2, 1.3, 1.4, 1.3, 1.2, 1.1, 1.2, 1.4];
    return seasonalFactors[month] || 1.0;
  };
  
  const getHourlyMultiplier = (hour) => {
    // Peak engagement hours: 9-11 AM, 1-3 PM, 7-9 PM
    if ((hour >= 9 && hour <= 11) || (hour >= 13 && hour <= 15) || (hour >= 19 && hour <= 21)) {
      return 1.3;
    } else if (hour >= 6 && hour <= 22) {
      return 1.1; // General daytime boost
    }
    return 0.9; // Night/early morning
  };
  
  const getPeakHours = (category) => {
    const peakHoursMap = {
      tech: "9 AM - 11 AM, 2 PM - 4 PM",
      content: "12 PM - 2 PM, 7 PM - 9 PM",
      work: "8 AM - 10 AM, 1 PM - 3 PM",
      lifestyle: "6 PM - 8 PM, 10 AM - 12 PM"
    };
    return peakHoursMap[category] || "7 PM - 9 PM";
  };

  // Helper function to assign platforms based on content category (kept for backward compatibility)
  const getPlatformsForTopic = (category) => {
    const platformMapping = {
      tech: ["YouTube", "Twitter", "LinkedIn"],
      work: ["LinkedIn", "Twitter", "Instagram"],
      content: ["TikTok", "Instagram", "YouTube"],
      lifestyle: ["Instagram", "TikTok", "YouTube"]
    };
    
    return platformMapping[category] || ["TikTok", "Instagram"];
  };
  
  // Time-based welcome message
  const getWelcomeMessage = () => {
    const userName = user.username || user.displayName || user.email?.split('@')[0] || 'Creator';
    const hour = new Date().getHours();
    var greeting = 'Good Evening';
    if (hour < 12) greeting = 'Good Morning';
    else if (hour < 17) greeting = 'Good Afternoon';
    return greeting + ', ' + userName;
  };

  const fetchRecentActivity = useCallback(async () => {
    try {
      // Fetch real activity from localStorage and server
      const userId = user.email || user.userId;
      const userActivityKey = `activity_${userId}`;
      const clearedAt = localStorage.getItem(`activity_cleared_${userId}`);
      let storedActivity = JSON.parse(localStorage.getItem(userActivityKey) || '[]');
      
      // Filter out activities that happened before the clear event
      if (clearedAt) {
        storedActivity = storedActivity.filter(a => new Date(a.time) > new Date(clearedAt));
      }
      
      // Add current login/navigation as new activity (only after clear)
      const now = new Date();
      const today = now.toDateString();
      const todayLogin = storedActivity.find(activity => 
        activity.type === 'login' && new Date(activity.time).toDateString() === today
      );
      
      if (!todayLogin) {
        const loginActivity = {
          type: "login",
          title: "Dashboard accessed",
          time: now.toISOString(),
          icon: "🔐"
        };
        storedActivity.unshift(loginActivity);
      }
      
      // Only add historical activities if not cleared recently
      if (!clearedAt) {
        // Add account creation activity if first time
        if (isFirstTimeLogin && !storedActivity.find(a => a.type === 'account_created')) {
          const accountActivity = {
            type: "account_created",
            title: user.isAuthenticated ? "Account created and verified" : "Started with Free Tier",
            time: now.toISOString(),
            icon: "🎉"
          };
          storedActivity.unshift(accountActivity);
        }
        
        // Add plan upgrade activity if applicable
        if (user.plan !== 'free' && !storedActivity.find(a => a.type === 'plan_upgrade')) {
          const planActivity = {
            type: "plan_upgrade",
            title: `Upgraded to ${getPlanDisplayName(user.plan)}`,
            time: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
            icon: "🚀"
          };
          storedActivity.push(planActivity);
        }
      }
      
      // Fetch video processing activity from recent usage
      const usageData = JSON.parse(localStorage.getItem('vlogclip_user') || '{}');
      if (usageData.usage?.videosThisMonth > 0) {
        const hasVideoActivity = storedActivity.find(a => a.type === 'video_processed');
        if (!hasVideoActivity) {
          const videoActivity = {
            type: "video_processed",
            title: `${usageData.usage.videosThisMonth} video${usageData.usage.videosThisMonth > 1 ? 's' : ''} processed this month`,
            time: new Date(Date.now() - 1000 * 60 * 30).toISOString(), // 30 minutes ago
            icon: "🎬"
          };
          storedActivity.push(videoActivity);
        }
      }
      
      if (usageData.usage?.clipsToday > 0) {
        const hasClipActivity = storedActivity.find(a => a.type === 'clips_generated');
        if (!hasClipActivity) {
          const clipActivity = {
            type: "clips_generated",
            title: `${usageData.usage.clipsToday} clips generated today`,
            time: new Date(Date.now() - 1000 * 60 * 10).toISOString(), // 10 minutes ago
            icon: "✨"
          };
          storedActivity.push(clipActivity);
        }
      }
      
      // Keep only last 10 activities and sort by time
      const sortedActivity = storedActivity
        .sort((a, b) => new Date(b.time) - new Date(a.time))
        .slice(0, 10);
      
      // Save back to localStorage
      localStorage.setItem(userActivityKey, JSON.stringify(sortedActivity));
      
      setRecentActivity(sortedActivity);
    } catch (error) {
      console.error('Failed to fetch real activity:', error);
      // Fallback to minimal real data
      setRecentActivity([
        {
          type: "login",
          title: "Dashboard accessed",
          time: new Date().toISOString(),
          icon: "🔐"
        }
      ]);
    }
  }, [user.email, user.userId, user.plan, user.isAuthenticated, isFirstTimeLogin]);

  // Removed fetchInboxMessages function - using direct email replies instead

  // Fetch real inbox messages from support replies
  const fetchInboxMessages = useCallback(async () => {
    try {
      const accountId = user.accountId;
      if (!accountId) return;
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/inbox/' + accountId, {
        headers: { 'Authorization': 'Bearer ' + (token || '') }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.messages && data.messages.length > 0) {
          const inboxNotifs = data.messages.map(function(msg) {
            return {
              id: 'inbox-' + msg.id,
              type: 'support_response',
              message: 'Support reply: ' + (msg.subject || 'New message'),
              time: msg.date ? new Date(msg.date).toLocaleString() : 'Recent',
              category: 'inbox',
              read: msg.status === 'read',
              fullMessage: msg.message,
              from: msg.from,
              actionable: true,
              target: 'notifications'
            };
          });
          setInboxMessages(inboxNotifs);
        }
      }
    } catch (err) {
      console.log('Inbox fetch error:', err.message);
    }
  }, [user.accountId]);

  const fetchNotifications = useCallback(async () => {
    setTimeout(() => {
      // Generate dynamic notifications based on AI insights
      const dynamicNotifications = [];
      
      if (aiInsights) {
        // Add AI-powered recommendations as notifications
        dynamicNotifications.push({
          id: `ai-rec-${Date.now()}`,
          type: "ai_insight",
          message: aiInsights.recommendation,
          time: "Updated today",
          category: "ai_insights",
          actionable: true,
          target: 'ai-insights'
        });

        // Add next best action as notification
        if (aiInsights.nextBestAction) {
          dynamicNotifications.push({
            id: `ai-action-${Date.now()}`,
            type: "tip",
            message: `AI Suggestion: ${aiInsights.nextBestAction}`,
            time: "Fresh insight",
            category: "optimization",
            actionable: true,
            target: user.plan === 'pro' || user.plan === 'business' || user.isAdmin ? 'batch' : 'generator'
          });
        }

        // Add viral potential alert if high
        if (aiInsights.viralPotential >= 85) {
          dynamicNotifications.push({
            id: `viral-alert-${Date.now()}`,
            type: "success",
            message: `🔥 High viral potential detected: ${aiInsights.viralPotential}% - Perfect time to create content!`,
            time: "Real-time",
            category: "viral_opportunity",
            actionable: true,
            target: user.plan === 'pro' || user.plan === 'business' || user.isAdmin ? 'batch' : 'generator'
          });
        }

        // Add trending topics notifications
        if (aiInsights.trendingTopics && aiInsights.trendingTopics.length > 0) {
          const topTrend = aiInsights.trendingTopics[0];
          dynamicNotifications.push({
            id: `trend-${Date.now()}`,
            type: "info",
            message: `📈 Trending Now: ${topTrend} - Create content around this topic for maximum reach`,
            time: "Trending",
            category: "trending",
            actionable: true,
            target: 'ai-insights'
          });
        }
      }

      // Dynamic system notifications powered by daily AI insights
      const postingTimes = aiInsights?.bestPostingTimes || null;
      const weekdayTip = aiInsights?.weekdayTip || null;
      const topTrends = aiInsights?.contentTrends || [];
      const contextualSystemNotifications = [
        { 
          id: `tip-${Date.now()}`, 
          type: "success", 
          message: weekdayTip || `Your monthly limits reset soon - Plan your viral content strategy`, 
          time: "Daily tip", 
          category: "system",
          actionable: true,
          target: 'ai-insights'
        },
        { 
          id: `trend-detail-${Date.now()}`, 
          type: "info", 
          message: topTrends.length > 1 ? `📊 ${topTrends[1].topic} is growing ${topTrends[1].growth} - Tap into this trend for maximum reach` : "Enhanced AI analysis now available - Get more accurate viral predictions", 
          time: "Fresh data", 
          category: "system",
          actionable: true,
          target: 'ai-insights'
        },
        { 
          id: `timing-${Date.now()}`, 
          type: "tip", 
          message: postingTimes ? `⏰ Best posting times today: Morning ${postingTimes.morning}, Peak at ${postingTimes.peak} - Schedule your content now` : `Optimal posting time: ${new Date().getHours() >= 17 ? 'Prime time detected' : 'Peak hours starting at 6 PM'} - Schedule your content now`, 
          time: "Smart timing", 
          category: "optimization",
          actionable: true,
          target: user.plan === 'pro' || user.plan === 'business' || user.isAdmin ? 'batch' : 'generator'
        }
      ];

      // Removed inbox notifications - using direct email replies

      // Combine all notifications including inbox messages
      const allNotifications = [
        ...inboxMessages.filter(function(m) { return !m.read; }),
        ...dynamicNotifications,
        ...contextualSystemNotifications.slice(0, 2)
      ].slice(0, 8);

      setNotifications(allNotifications);
    }, 400);
  }, [aiInsights, onNavigateToApp]);

  // Update notifications when AI insights change
  useEffect(() => {
    fetchNotifications();
    fetchInboxMessages();
  }, [aiInsights, fetchNotifications, fetchInboxMessages]);

  // Poll inbox every 10 seconds + refresh on page visibility
  useEffect(() => {
    const inboxInterval = setInterval(fetchInboxMessages, 10000);
    const handleVisibility = () => {
      if (!document.hidden) { fetchInboxMessages(); }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(inboxInterval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchInboxMessages]);

  // Poll processing status every 3 seconds — shows live progress on dashboard
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const checkProcessing = async () => {
      try {
        // Check active batch jobs first (most common for Pro/Business users)
        try {
          const br = await fetch('/api/batch/jobs');
          if (br.ok) {
            const jobs = await br.json();
            const userJob = (Array.isArray(jobs) ? jobs : []).find(j => j.userEmail === user?.email && (j.status === 'processing' || j.status === 'queued'));
            if (userJob) {
              const sr = await fetch('/api/batch/status/' + userJob.jobId);
              if (sr.ok) {
                const sd = await sr.json();
                setProcessingStatus({ type: 'batch', jobId: userJob.jobId, ...sd });
                return;
              }
            }
          }
        } catch(e) {}
        // Check queue jobs for this user
        const pendingJobs = user?.pendingJobs || [];
        for (const job of pendingJobs.slice(-3)) {
          try {
            const qr = await fetch('/api/queue/status/' + job.jobId, { headers: { 'Authorization': 'Bearer ' + (token || '') } });
            if (qr.ok) {
              const qd = await qr.json();
              if (qd.status === 'queued' || qd.status === 'processing') {
                setProcessingStatus({ type: 'queue', jobId: job.jobId, ...qd });
                return;
              }
            }
          } catch(e) {}
        }
        // Check single video progress (global — only show if user has an active generation)
        try {
          const r = await fetch('/api/progress', { headers: { 'Authorization': 'Bearer ' + (token || '') } });
          if (r.ok) {
            const data = await r.json();
            if (data.status === 'processing' && data.progress > 0 && data.progress < 100) {
              setProcessingStatus({ type: 'single', ...data });
              return;
            }
          }
        } catch(e) {}
        setProcessingStatus(null);
      } catch(e) { setProcessingStatus(null); }
    };
    checkProcessing();
    const interval = setInterval(checkProcessing, 3000);
    return () => clearInterval(interval);
  }, [user?.email, user?.pendingJobs]);

  const handleLogout = () => {
    logout();
  };

  const getPlanColor = (plan) => {
    if (user.isAdmin) return '#dc2626'; // Red for admin
    switch (plan) {
      case 'free': return '#6b7280';
      case 'pro': return '#8b5cf6';
      case 'business': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getPlanFeatures = (plan) => {
    if (user.isAdmin) {
      return [
        '🔧 Full system access',
        '⚡ Unlimited everything',
        '📊 All analytics',
        '🛠️ Admin privileges',
        '🚀 Priority processing',
        '🔒 Master account'
      ];
    }
    
    const features = {
      free: [
        '5 videos per month',
        '15 clips per month',
        'Standard quality',
        'Basic support'
      ],
      pro: [
        'Unlimited videos',
        'Unlimited clips',
        'High quality export',
        'Priority support',
        'Bulk processing'
      ],
      business: [
        'Everything in Pro',
        'Advanced analytics',
        'Commercial rights',
        'Team collaboration',
        'API access',
        'White-label options'
      ]
    };
    return features[plan] || features.free;
  };

  if (isLoading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTimeOfDayIcon = () => {
    const hour = currentTime.getHours();
    const baseStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      marginRight: '8px',
      fontSize: '16px',
      fontWeight: '700',
      textShadow: '0 0 8px rgba(255,255,255,0.3)',
      animation: 'pulse 2s infinite'
    };

    if (hour >= 5 && hour < 12) {
      // Morning - Aurora Quantum style
      return (
        <span style={{
          ...baseStyle,
          background: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 50%, #ff9ff3 100%)',
          border: '2px solid rgba(255, 107, 107, 0.5)',
          boxShadow: '0 0 25px rgba(255, 107, 107, 0.7), 0 0 40px rgba(254, 202, 87, 0.4), inset 0 0 20px rgba(255, 255, 255, 0.2)',
          color: '#ffffff',
          position: 'relative'
        }}>
          ⬢
        </span>
      );
    } else if (hour >= 12 && hour < 17) {
      // Daytime - Neural Solar style  
      return (
        <span style={{
          ...baseStyle,
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 50%, #4facfe 100%)',
          border: '2px solid rgba(240, 147, 251, 0.6)',
          boxShadow: '0 0 30px rgba(240, 147, 251, 0.8), 0 0 50px rgba(245, 87, 108, 0.5), inset 0 0 25px rgba(255, 255, 255, 0.25)',
          color: '#ffffff'
        }}>
          ⬟
        </span>
      );
    } else if (hour >= 17 && hour < 20) {
      // Evening - Holographic Sunset style
      return (
        <span style={{
          ...baseStyle,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
          border: '2px solid rgba(102, 126, 234, 0.6)',
          boxShadow: '0 0 28px rgba(102, 126, 234, 0.8), 0 0 45px rgba(118, 75, 162, 0.6), inset 0 0 22px rgba(255, 255, 255, 0.18)',
          color: '#ffffff'
        }}>
          ⬡
        </span>
      );
    } else {
      // Night - Cyber Void style
      return (
        <span style={{
          ...baseStyle,
          background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 50%, #9b59b6 100%)',
          border: '2px solid rgba(44, 62, 80, 0.7)',
          boxShadow: '0 0 20px rgba(52, 152, 219, 0.6), 0 0 35px rgba(155, 89, 182, 0.4), inset 0 0 18px rgba(255, 255, 255, 0.15)',
          color: '#ffffff'
        }}>
          ⬣
        </span>
      );
    }
  };

  const getUpgradeTarget = (currentPlan) => {
    if (currentPlan === 'free' || currentPlan === 'starter') return 'pro';
    if (currentPlan === 'pro') return 'business';
    return null;
  };

  const getDowngradeTarget = (currentPlan) => {
    if (currentPlan === 'business') return 'pro';
    if (currentPlan === 'pro') return 'free';
    return null;
  };

  const handlePlanNavigation = (targetPlan) => {
    // Record navigation activity
    recordActivity('navigation', `Viewed ${getPlanDisplayName(targetPlan)} details`);
    // Navigate to plan-specific page
    onNavigateToApp('plan-' + targetPlan);
  };

  const recordActivity = (type, title, icon = '📝') => {
    const userId = user.email || user.userId;
    const userActivityKey = `activity_${userId}`;
    const storedActivity = JSON.parse(localStorage.getItem(userActivityKey) || '[]');
    
    const newActivity = {
      type,
      title,
      time: new Date().toISOString(),
      icon
    };
    
    storedActivity.unshift(newActivity);
    const limitedActivity = storedActivity.slice(0, 10);
    localStorage.setItem(userActivityKey, JSON.stringify(limitedActivity));
    
    // Update state immediately
    setRecentActivity(limitedActivity);
  };

  return (
    <div className="user-dashboard">
      {/* Stylish Clock and Plan Bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 24px',
        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(99, 102, 241, 0.2)',
        borderRadius: '16px',
        marginBottom: '24px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Clock */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start'
        }}>
          <div style={{
            fontSize: '24px',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #e2e8f0 0%, #a78bfa 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            letterSpacing: '1px'
          }}>
{getTimeOfDayIcon()} {formatTime(currentTime)}
          </div>
          <div style={{
            fontSize: '12px',
            color: '#94a3b8',
            fontWeight: '500',
            marginTop: '2px'
          }}>
            {formatDate(currentTime)}
          </div>
        </div>

        {/* Country Flag */}
        {user.countryCode && user.countryCode !== 'XX' && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '4px 16px'
          }}>
            <span style={{fontSize:'32px',lineHeight:'1'}}>
              {String.fromCodePoint(...[...user.countryCode.toUpperCase()].map(c => 0x1F1E6 - 65 + c.charCodeAt(0)))}
            </span>
            <span style={{fontSize:'13px',color:'#e2e8f0',marginTop:'4px',fontWeight:'700'}}>
              {user.country || ''}
            </span>
          </div>
        )}
        {/* Plan Navigation Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'center'
        }}>
          {/* Current Plan Button */}
          <button
            onClick={() => handlePlanNavigation(user.plan)}
            style={{
              padding: '10px 16px',
              background: `linear-gradient(135deg, ${getPlanColor(user.plan)}20 0%, ${getPlanColor(user.plan)}10 100%)`,
              border: `2px solid ${getPlanColor(user.plan)}40`,
              borderRadius: '12px',
              color: '#ffffff',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = `0 8px 25px ${getPlanColor(user.plan)}30`;
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            {user.isAdmin ? '👑 Your Admin Plan' : `📋 Your ${getPlanDisplayName(user.plan)}`}
          </button>

          {/* Upgrade Button */}
          {!user.isAdmin && getUpgradeTarget(user.plan) && (
            <button
              onClick={() => onNavigateToApp('upgrade')}
              style={{
                padding: '10px 16px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                border: '2px solid rgba(16, 185, 129, 0.4)',
                borderRadius: '12px',
                color: '#ffffff',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 25px rgba(16, 185, 129, 0.4)';
                e.target.style.background = 'linear-gradient(135deg, #059669 0%, #047857 100%)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
                e.target.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
              }}
            >
              ⬆️ UPGRADE
            </button>
          )}

          {/* Downgrade Button - Business users only */}
          {!user.isAdmin && user.plan === 'business' && (
            <button
              onClick={() => onNavigateToApp('upgrade')}
              style={{
                padding: '10px 16px',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                border: '2px solid rgba(245, 158, 11, 0.4)',
                borderRadius: '12px',
                color: '#ffffff',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 25px rgba(245, 158, 11, 0.4)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              ⬇️ DOWNGRADE TO PRO
            </button>
          )}
          

          {/* Free Tier Button (always available) */}
          
        </div>
      </div>

      <div className="dashboard-header">
        <div className="user-info">
          <h1>{getWelcomeMessage()}</h1>
          <p className="user-email">{user.email}</p>
          {notifications.length > 0 && (
            <div 
              style={{ 
                marginTop: '8px', 
                fontSize: '14px', 
                color: '#10b981',
                cursor: 'pointer',
                textDecoration: 'underline',
                display: 'flex',
                gap: '16px',
                flexWrap: 'wrap'
              }}
            >
              {/* Support replies shown below, AI insights link separate */}
              {inboxMessages.filter(function(m) { return !m.read; }).length > 0 && (
                <span onClick={() => onNavigateToApp('notifications')} style={{ color: '#f59e0b', cursor: 'pointer', textDecoration: 'underline' }}>
                  {'📬 ' + inboxMessages.filter(function(m) { return !m.read; }).length + ' new support repl' + (inboxMessages.filter(function(m) { return !m.read; }).length !== 1 ? 'ies' : 'y')}
                </span>
              )}
              {aiInsights && (
                <span onClick={() => onNavigateToApp('ai-insights')} style={{ color: '#8b5cf6' }}>
                  🤖 New AI insight available
                </span>
              )}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <div style={{ 
            padding: '8px 16px', 
            background: presenceStatus === 'online' ? 'rgba(16, 185, 129, 0.1)' : presenceStatus === 'away' ? 'rgba(234, 179, 8, 0.1)' : 'rgba(239, 68, 68, 0.1)', 
            border: '1px solid ' + (presenceStatus === 'online' ? 'rgba(16, 185, 129, 0.3)' : presenceStatus === 'away' ? 'rgba(234, 179, 8, 0.3)' : 'rgba(239, 68, 68, 0.3)'),
            borderRadius: '8px',
            color: presenceStatus === 'online' ? '#10b981' : presenceStatus === 'away' ? '#eab308' : '#ef4444',
            fontSize: '14px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <span style={{width:'8px',height:'8px',borderRadius:'50%',display:'inline-block',background: presenceStatus === 'online' ? '#10b981' : presenceStatus === 'away' ? '#eab308' : '#ef4444',boxShadow: '0 0 6px ' + (presenceStatus === 'online' ? 'rgba(16,185,129,0.6)' : presenceStatus === 'away' ? 'rgba(234,179,8,0.6)' : 'rgba(239,68,68,0.6)')}}></span>
            {presenceStatus === 'online' ? 'Online' : presenceStatus === 'away' ? 'Away' : 'Offline'}
          </div>
          <button onClick={()=>setShowPasswordChange(!showPasswordChange)} style={{padding:'8px 14px',background:'rgba(139,92,246,0.1)',border:'1px solid rgba(139,92,246,0.25)',borderRadius:'8px',color:'#a78bfa',fontSize:'12px',fontWeight:'600',cursor:'pointer',transition:'all 0.2s',whiteSpace:'nowrap'}}>
            🔑 Password
          </button>
          <button className="logout-btn" onClick={handleLogout}>
            Sign Out
          </button>
        </div>
        {showPasswordChange && (
          <div className="pw-change-panel" style={{padding:'20px 24px',background:'linear-gradient(135deg,rgba(15,23,42,0.95),rgba(30,27,75,0.9))',border:'1px solid rgba(139,92,246,0.2)',borderRadius:'0 0 14px 14px',marginTop:'-1px',backdropFilter:'blur(8px)'}}>
            <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'14px'}}>
              <span style={{fontSize:'16px'}}>🔐</span>
              <span style={{color:'#e2e8f0',fontSize:'14px',fontWeight:'700'}}>Change Password</span>
              <button onClick={()=>setShowPasswordChange(false)} style={{marginLeft:'auto',background:'rgba(255,255,255,0.06)',border:'none',borderRadius:'50%',width:'26px',height:'26px',color:'#64748b',fontSize:'16px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>×</button>
            </div>
            <div className="pw-change-fields" style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px',marginBottom:'14px'}}>
              <div>
                <label style={{display:'block',color:'#94a3b8',fontSize:'11px',fontWeight:'600',marginBottom:'4px',textTransform:'uppercase',letterSpacing:'0.5px'}}>Current Password</label>
                <input type="password" value={pwCurrent} onChange={e=>setPwCurrent(e.target.value)} placeholder="Enter current" style={{width:'100%',padding:'10px 12px',borderRadius:'10px',border:'1px solid rgba(139,92,246,0.2)',background:'rgba(255,255,255,0.04)',color:'#e2e8f0',fontSize:'13px',boxSizing:'border-box',outline:'none',transition:'border-color 0.2s'}} onFocus={e=>{e.target.style.borderColor='rgba(139,92,246,0.5)';}} onBlur={e=>{e.target.style.borderColor='rgba(139,92,246,0.2)';}} />
              </div>
              <div>
                <label style={{display:'block',color:'#94a3b8',fontSize:'11px',fontWeight:'600',marginBottom:'4px',textTransform:'uppercase',letterSpacing:'0.5px'}}>New Password</label>
                <input type="password" value={pwNew} onChange={e=>setPwNew(e.target.value)} placeholder="Min 6 characters" style={{width:'100%',padding:'10px 12px',borderRadius:'10px',border:'1px solid rgba(139,92,246,0.2)',background:'rgba(255,255,255,0.04)',color:'#e2e8f0',fontSize:'13px',boxSizing:'border-box',outline:'none',transition:'border-color 0.2s'}} onFocus={e=>{e.target.style.borderColor='rgba(139,92,246,0.5)';}} onBlur={e=>{e.target.style.borderColor='rgba(139,92,246,0.2)';}} />
              </div>
              <div>
                <label style={{display:'block',color:'#94a3b8',fontSize:'11px',fontWeight:'600',marginBottom:'4px',textTransform:'uppercase',letterSpacing:'0.5px'}}>Confirm Password</label>
                <input type="password" value={pwConfirm} onChange={e=>setPwConfirm(e.target.value)} placeholder="Re-enter new" style={{width:'100%',padding:'10px 12px',borderRadius:'10px',border:'1px solid rgba(139,92,246,0.2)',background:'rgba(255,255,255,0.04)',color:'#e2e8f0',fontSize:'13px',boxSizing:'border-box',outline:'none',transition:'border-color 0.2s'}} onFocus={e=>{e.target.style.borderColor='rgba(139,92,246,0.5)';}} onBlur={e=>{e.target.style.borderColor='rgba(139,92,246,0.2)';}} />
              </div>
            </div>
            <button onClick={handleChangePassword} disabled={pwLoading||!pwCurrent||!pwNew||!pwConfirm} style={{padding:'10px 24px',borderRadius:'10px',border:'none',background:pwCurrent&&pwNew&&pwConfirm&&!pwLoading?'linear-gradient(135deg,#8b5cf6,#7c3aed)':'rgba(255,255,255,0.06)',color:pwCurrent&&pwNew&&pwConfirm&&!pwLoading?'white':'#475569',fontWeight:'700',cursor:pwCurrent&&pwNew&&pwConfirm&&!pwLoading?'pointer':'default',fontSize:'13px',transition:'all 0.2s',boxShadow:pwCurrent&&pwNew&&pwConfirm&&!pwLoading?'0 2px 10px rgba(139,92,246,0.3)':'none'}}>{pwLoading?'Updating...':'Update Password'}</button>
            {pwMsg && <p style={{color:pwMsg.startsWith('✅')?'#34d399':'#f87171',fontSize:'12px',marginTop:'10px',marginBottom:0}}>{pwMsg}</p>}
          </div>
        )}
      </div>

      {/* Payment Success Modal */}
      {showPaymentSuccess && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.75)',zIndex:10000,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px',backdropFilter:'blur(6px)'}} onClick={()=>setShowPaymentSuccess(false)}>
          <div onClick={e=>e.stopPropagation()} style={{background:'linear-gradient(135deg,#1a1a2e 0%,#16213e 100%)',borderRadius:'20px',border:'1px solid rgba(139,92,246,0.3)',maxWidth:'460px',width:'100%',overflow:'hidden',boxShadow:'0 20px 60px rgba(0,0,0,0.5)'}}>
            <div style={{background:'linear-gradient(135deg,#8b5cf6,#06b6d4)',padding:'32px 24px',textAlign:'center'}}>
              <div style={{fontSize:'56px',marginBottom:'8px'}}>🎉</div>
              <h2 style={{color:'white',fontSize:'24px',fontWeight:'800',margin:'0 0 6px'}}>Welcome to {paymentPlan}!</h2>
              <p style={{color:'rgba(255,255,255,0.85)',fontSize:'14px',margin:0}}>Your plan is now active</p>
            </div>
            <div style={{padding:'28px 24px'}}>
              <div style={{display:'flex',flexDirection:'column',gap:'10px',marginBottom:'24px'}}>
                {(paymentPlan === 'Business' ? [
                  ['🎬','8 clips per video in 1080p HD'],
                  ['📦','Batch process 4 videos at once'],
                  ['⏱','60-second clips'],
                  ['🎨','Custom branding on all clips'],
                  ['🌐','Caption translation in 31 languages'],
                  ['🔥','AI hooks, emoji overlays, engagement badges'],
                  ['💾','10-hour clip storage']
                ] : [
                  ['🎬','5 clips per video in 1080p HD'],
                  ['📦','Batch process 3 videos at once'],
                  ['⏱','30-second clips'],
                  ['🌐','Caption translation in 31 languages'],
                  ['🔥','AI hooks, emoji overlays, engagement badges'],
                  ['💾','5-hour clip storage'],
                  ['✨','No watermark on clips']
                ]).map(([icon, text], i) => (
                  <div key={i} style={{display:'flex',alignItems:'center',gap:'10px',padding:'8px 12px',background:'rgba(139,92,246,0.06)',borderRadius:'8px'}}>
                    <span style={{fontSize:'16px',flexShrink:0}}>{icon}</span>
                    <span style={{color:'#e2e8f0',fontSize:'13px',fontWeight:'500'}}>{text}</span>
                  </div>
                ))}
              </div>
              <button onClick={()=>setShowPaymentSuccess(false)} style={{width:'100%',padding:'14px',background:'linear-gradient(135deg,#8b5cf6,#7c3aed)',border:'none',borderRadius:'12px',color:'white',fontSize:'15px',fontWeight:'700',cursor:'pointer',boxShadow:'0 4px 15px rgba(139,92,246,0.4)',transition:'all 0.2s'}}>
                Start Creating Clips
              </button>
              <p style={{color:'#64748b',fontSize:'11px',textAlign:'center',margin:'14px 0 0'}}>A receipt has been emailed to you from Stripe</p>
            </div>
          </div>
        </div>
      )}

      <DashboardMobileNav activeTab={mobileTab} onTabChange={(tab) => {
        if (tab === 'help') {
          window.dispatchEvent(new CustomEvent('open-help-chat'));
          return;
        }
        setMobileTab(tab);
        const targets = { overview: 'dash-plan', generate: 'dash-actions', clips: 'dash-clips', analytics: 'dash-analytics', settings: 'dash-settings' };
        const el = document.getElementById(targets[tab]);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }} />
      <div className="dashboard-content">
        {/* Live Processing Status */}
        {processingStatus && (
          <div style={{
            margin:'0 0 20px',padding:'20px 24px',
            background:'linear-gradient(135deg,rgba(99,102,241,0.15),rgba(139,92,246,0.1))',
            borderRadius:'16px',border:'1px solid rgba(139,92,246,0.3)',
            boxShadow:'0 4px 24px rgba(99,102,241,0.15)',
            animation:'pulse 2s ease-in-out infinite'
          }}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'12px'}}>
              <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                <div style={{width:'12px',height:'12px',borderRadius:'50%',background:'#10b981',boxShadow:'0 0 8px rgba(16,185,129,0.6)',animation:'pulse 1.5s ease-in-out infinite'}}></div>
                <span style={{color:'#e2e8f0',fontWeight:'700',fontSize:'15px'}}>
                  {processingStatus.type === 'batch' ? 'Batch Processing' : processingStatus.type === 'queue' && processingStatus.status === 'queued' ? 'In Queue' : 'Processing Video'}
                </span>
              </div>
              <span style={{color:'#a78bfa',fontWeight:'800',fontSize:'20px'}}>
                {processingStatus.status === 'queued' ? '#' + (processingStatus.position || '?') : (processingStatus.progress || 0) + '%'}
              </span>
            </div>
            {processingStatus.status !== 'queued' && (
              <div style={{width:'100%',height:'8px',background:'rgba(255,255,255,0.08)',borderRadius:'4px',overflow:'hidden',marginBottom:'10px'}}>
                <div style={{
                  height:'100%',borderRadius:'4px',transition:'width 0.5s ease',
                  background:'linear-gradient(90deg,#8b5cf6,#6366f1,#8b5cf6)',
                  backgroundSize:'200% 100%',animation:'shimmer 2s linear infinite',
                  width:(processingStatus.progress || 0) + '%'
                }}></div>
              </div>
            )}
            <p style={{color:'#94a3b8',fontSize:'13px',margin:0,fontWeight:'500'}}>
              {processingStatus.status === 'queued'
                ? 'Waiting in queue — estimated ' + (processingStatus.estimatedWaitMins || '?') + ' min. You can close this page — we\'ll email you when done!'
                : processingStatus.message || 'Processing your video...'}
            </p>
            {processingStatus.type === 'batch' && processingStatus.videos && (
              <div style={{marginTop:'10px',display:'flex',gap:'6px',flexWrap:'wrap'}}>
                {processingStatus.videos.map((v, i) => (
                  <div key={i} style={{
                    padding:'4px 10px',borderRadius:'6px',fontSize:'11px',fontWeight:'600',
                    background: v.status === 'completed' ? 'rgba(16,185,129,0.2)' : v.status === 'processing' ? 'rgba(99,102,241,0.2)' : v.status === 'error' ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.05)',
                    color: v.status === 'completed' ? '#34d399' : v.status === 'processing' ? '#a78bfa' : v.status === 'error' ? '#f87171' : '#64748b',
                    border: '1px solid ' + (v.status === 'completed' ? 'rgba(16,185,129,0.3)' : v.status === 'processing' ? 'rgba(99,102,241,0.3)' : v.status === 'error' ? 'rgba(239,68,68,0.3)' : 'rgba(255,255,255,0.08)')
                  }}>
                    Video {i+1}: {v.status === 'completed' ? 'Done' : v.status === 'processing' ? Math.round(v.progress || 0) + '%' : v.status === 'error' ? 'Failed' : 'Waiting'}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* Plan Status Card */}
        <div className="dashboard-card plan-status">
          <div className="card-header">
            <h2 id="dash-plan">🚀 Your Plan</h2>
            <span 
              className="plan-badge" 
              style={{ backgroundColor: getPlanColor(user.plan) }}
            >
              {user.isAdmin ? '👑 ADMIN' : user.plan.charAt(0).toUpperCase() + user.plan.slice(1)}
            </span>

          </div>
          
          <div className="plan-features">
            <h3>✨ Plan Features:</h3>
            <ul>
              {getPlanFeatures(user.plan).map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </ul>
          </div>

          {/* Videos Remaining - Premium Traffic Light */}
          {(() => {
            const baseLimit = user.isAdmin ? Infinity : user.plan === 'business' ? 60 : user.plan === 'pro' ? 20 : 5;
            const referralBonus = analytics?.referralCredits || 0;
            const limit = user.isAdmin ? Infinity : baseLimit + referralBonus;
            const used = analytics?.monthlyUsage || 0;
            const remaining = user.isAdmin ? Infinity : Math.max(0, limit - used);
            const usedPct = user.isAdmin ? 0 : Math.min(100, (used / limit) * 100);
            const remainPct = user.isAdmin ? 100 : Math.max(0, 100 - usedPct);
            const accentColor = usedPct >= 100 ? '#ef4444' : usedPct >= 75 ? '#f97316' : usedPct >= 50 ? '#eab308' : '#10b981';
            const accentGlow = usedPct >= 100 ? 'rgba(239,68,68,0.4)' : usedPct >= 75 ? 'rgba(249,115,22,0.4)' : usedPct >= 50 ? 'rgba(234,179,8,0.4)' : 'rgba(16,185,129,0.4)';
            const barGrad = usedPct >= 100 ? 'linear-gradient(90deg,#dc2626,#ef4444,#dc2626)' :
              usedPct >= 75 ? 'linear-gradient(90deg,#ea580c,#f97316,#ea580c)' :
              usedPct >= 50 ? 'linear-gradient(90deg,#ca8a04,#eab308,#ca8a04)' :
              'linear-gradient(90deg,#059669,#10b981,#34d399)';
            const statusLabel = usedPct >= 100 ? 'DEPLETED' : usedPct >= 75 ? 'LOW' : usedPct >= 50 ? 'MODERATE' : 'AVAILABLE';
            return (
              <div style={{margin:'16px 0',padding:'20px',background:'linear-gradient(135deg,rgba(25,25,50,0.95),rgba(40,37,90,0.95))',borderRadius:'16px',border:'1px solid rgba(255,255,255,0.08)',boxShadow:'0 4px 24px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.05)'}}>
                {/* Header */}
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px'}}>
                  <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                    <div style={{width:'36px',height:'36px',borderRadius:'10px',background:'linear-gradient(135deg,' + accentColor + ',' + accentColor + '88)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'18px',boxShadow:'0 2px 8px ' + accentGlow}}>📹</div>
                    <div>
                      <div style={{color:'#ffffff',fontWeight:'700',fontSize:'14px',letterSpacing:'0.3px'}}>Video Credits</div>
                      <div style={{color:'#64748b',fontSize:'11px',fontWeight:'500',marginTop:'1px'}}>Resets monthly{referralBonus > 0 && <span style={{color:'#10b981',fontWeight:'700'}}>{' '}(+{referralBonus} bonus)</span>}</div>
                    </div>
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div style={{color:'#ffffff',fontSize:'24px',fontWeight:'800',lineHeight:'1'}}>{user.isAdmin ? '∞' : remaining}</div>
                    <div style={{color:'#64748b',fontSize:'11px',fontWeight:'500',marginTop:'2px'}}>{user.isAdmin ? 'unlimited' : remaining + ' of ' + limit + ' remaining'}</div>
                  </div>
                </div>
                {/* Progress Bar - Thick with count inside */}
                <div style={{position:'relative',width:'100%',height:'36px',background:'rgba(255,255,255,0.04)',borderRadius:'12px',overflow:'hidden',marginBottom:'14px',border:'1px solid rgba(255,255,255,0.06)'}}>
                  <div style={{position:'absolute',top:0,left:0,height:'100%',borderRadius:'12px',transition:'width 0.6s cubic-bezier(0.4,0,0.2,1)',background:barGrad,width:user.isAdmin ? '100%' : Math.max(usedPct > 0 ? 15 : 0, usedPct) + '%',boxShadow:'0 0 16px ' + accentGlow + ', inset 0 1px 0 rgba(255,255,255,0.15)',display:'flex',alignItems:'center',justifyContent:'center',minWidth: usedPct > 0 ? '60px' : '0'}}>
                    {usedPct > 0 && <span style={{color:'#fff',fontSize:'12px',fontWeight:'800',textShadow:'0 1px 2px rgba(0,0,0,0.3)',letterSpacing:'0.5px'}}>{used} / {user.isAdmin ? '∞' : limit}</span>}
                  </div>
                  {usedPct === 0 && <div style={{position:'absolute',top:0,left:0,width:'100%',height:'100%',display:'flex',alignItems:'center',justifyContent:'center'}}><span style={{color:'rgba(255,255,255,0.3)',fontSize:'12px',fontWeight:'600'}}>0 / {user.isAdmin ? '∞' : limit} used</span></div>}
                </div>
                {/* Bottom Stats */}
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                    <div style={{width:'8px',height:'8px',borderRadius:'50%',background:accentColor,boxShadow:'0 0 6px ' + accentGlow}}></div>
                    <span style={{color:accentColor,fontSize:'11px',fontWeight:'700',letterSpacing:'0.8px',textTransform:'uppercase'}}>{statusLabel}</span>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
                    <span style={{color:'#94a3b8',fontSize:'12px',fontWeight:'600'}}>{used} used</span>
                    <div style={{background:'rgba(255,255,255,0.06)',padding:'3px 10px',borderRadius:'20px',border:'1px solid rgba(255,255,255,0.08)'}}>
                      <span style={{color:'#ffffff',fontSize:'12px',fontWeight:'700'}}>{user.isAdmin ? '∞' : Math.round(remainPct)}%</span>
                    </div>
                  </div>
                </div>
                {/* Reset Countdown Timer */}
                {!user.isAdmin && analytics?.nextResetDate && (() => {
                  const now = new Date();
                  const resetDate = new Date(analytics.nextResetDate);
                  const diff = resetDate.getTime() - now.getTime();
                  if (diff <= 0) return <div style={{marginTop:'10px',padding:'8px 12px',background:'rgba(16,185,129,0.1)',borderRadius:'8px',border:'1px solid rgba(16,185,129,0.2)',display:'flex',alignItems:'center',gap:'8px'}}><span style={{fontSize:'12px'}}>🔄</span><span style={{color:'#10b981',fontSize:'12px',fontWeight:'700'}}>Credits refreshing now...</span></div>;
                  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                  const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                  return (
                    <div style={{marginTop:'10px',padding:'10px 14px',background:'rgba(255,255,255,0.03)',borderRadius:'10px',border:'1px solid rgba(255,255,255,0.06)',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:'8px'}}>
                      <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                        <span style={{fontSize:'13px'}}>⏱️</span>
                        <span style={{color:'#94a3b8',fontSize:'12px',fontWeight:'600'}}>Credits refresh in</span>
                      </div>
                      <div style={{display:'flex',gap:'6px'}}>
                        <div style={{background:'rgba(99,102,241,0.15)',padding:'4px 8px',borderRadius:'6px',textAlign:'center',minWidth:'40px'}}>
                          <div style={{color:'#fff',fontSize:'14px',fontWeight:'800',lineHeight:'1'}}>{days}</div>
                          <div style={{color:'#64748b',fontSize:'9px',fontWeight:'600',marginTop:'2px'}}>DAYS</div>
                        </div>
                        <div style={{background:'rgba(99,102,241,0.15)',padding:'4px 8px',borderRadius:'6px',textAlign:'center',minWidth:'40px'}}>
                          <div style={{color:'#fff',fontSize:'14px',fontWeight:'800',lineHeight:'1'}}>{hours}</div>
                          <div style={{color:'#64748b',fontSize:'9px',fontWeight:'600',marginTop:'2px'}}>HRS</div>
                        </div>
                        <div style={{background:'rgba(99,102,241,0.15)',padding:'4px 8px',borderRadius:'6px',textAlign:'center',minWidth:'40px'}}>
                          <div style={{color:'#fff',fontSize:'14px',fontWeight:'800',lineHeight:'1'}}>{mins}</div>
                          <div style={{color:'#64748b',fontSize:'9px',fontWeight:'600',marginTop:'2px'}}>MIN</div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })()}
          {user.plan === 'free' && (
            <div className="upgrade-prompt">
              <p>🌟 Ready to unlock more features?</p>
              <button className="upgrade-btn" onClick={() => onNavigateToApp("plan-pro")}>🚀 UPGRADE TO PRO</button>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="dashboard-card quick-actions">
          <h2 id="dash-actions">⚡ Quick Actions</h2>
          <div className="action-buttons">
            <button 
              className="action-btn primary"
              onClick={() => {
                recordActivity('navigation', `Accessed ${user.isAdmin ? 'Admin' : getPlanDisplayName(user.plan)} Home Page`, '🏠');
                // Map starter plan to free for navigation, admin to business
                let planForNavigation = user.plan;
                if (user.plan === 'starter') planForNavigation = 'free';
                if (user.isAdmin) planForNavigation = 'business';
                
                console.log('🏠 Home navigation:', { originalPlan: user.plan, mappedPlan: planForNavigation, isAdmin: user.isAdmin });
                onNavigateToApp('plan-' + planForNavigation);
              }}
            >
              <span className="btn-icon">🏠</span>
              <div>
                <span className="btn-title">Home Page</span>
                <span className="btn-subtitle">{user.isAdmin ? 'Admin Dashboard' : `${getPlanDisplayName(user.plan)} Features`}</span>
              </div>
            </button>
            
            {/* Create Clips button only for Free/Starter tier */}
            {(user.plan === 'free' || user.plan === 'starter') && (
              <button 
                className="action-btn primary"
                onClick={() => {
                  recordActivity('feature_access', 'Started creating video clips', '🎬');
                  onNavigateToApp('generator');
                }}
              >
                <span className="btn-icon">🎬</span>
                <div>
                  <span className="btn-title">Create Clips</span>
                  <span className="btn-subtitle">Generate viral clips from videos</span>
                </div>
              </button>
            )}
            
            {user.isAdmin || user.plan === 'pro' || user.plan === 'business' ? (
              <button 
                className="action-btn"
                onClick={() => {
                  recordActivity('feature_access', 'Started batch video processing', '⚡');
                  onNavigateToApp('batch');
                }}
              >
                <span className="btn-icon">⚡</span>
                <div>
                  <span className="btn-title">Batch Processing</span>
                  <span className="btn-subtitle">Process multiple videos at once</span>
                </div>
              </button>
            ) : (
              <button className="action-btn disabled">
                <span className="btn-icon">🔒</span>
                <div>
                  <span className="btn-title">Batch Processing</span>
                  <span className="btn-subtitle">Upgrade to Pro to unlock</span>
                </div>
              </button>
            )}
            
            {user.isAdmin || user.plan === 'business' ? (
              <button 
                className="action-btn"
                onClick={() => {
                  recordActivity('feature_access', 'Viewed advanced analytics dashboard', '📊');
                  onNavigateToApp('analytics');
                }}
              >
                <span className="btn-icon">📊</span>
                <div>
                  <span className="btn-title">Advanced Analytics</span>
                  <span className="btn-subtitle">Deep insights and reporting</span>
                </div>
              </button>
            ) : (
              <button className="action-btn disabled">
                <span className="btn-icon">🔒</span>
                <div>
                  <span className="btn-title">Advanced Analytics</span>
                  <span className="btn-subtitle">Upgrade to Business to unlock</span>
                </div>
              </button>
            )}
            
            <button 
              className="action-btn"
              onClick={() => user.isAdmin ? onNavigateToApp('api') : alert('🔌 API Access is coming soon! This feature will be available for Business plan users in a future update.')}
              style={!user.isAdmin ? {opacity: 0.6, cursor: 'not-allowed'} : {}}
            >
              <span className="btn-icon">🔌</span>
              <div>
                <span className="btn-title">API Access</span>
                <span className="btn-subtitle">{user.isAdmin ? 'Integrate with your workflow' : '🔒 Coming Soon'}</span>
              </div>
            </button>

            <button 
              className="action-btn"
              onClick={() => onNavigateToApp('documentation')}
            >
              <span className="btn-icon">📚</span>
              <div>
                <span className="btn-title">Documentation</span>
                <span className="btn-subtitle">Learn tips and best practices</span>
              </div>
            </button>

            <button 
              className="action-btn"
              onClick={() => onNavigateToApp('support')}
            >
              <span className="btn-icon">💬</span>
              <div>
                <span className="btn-title">Support Center</span>
                <span className="btn-subtitle">Get help from our team</span>
              </div>
            </button>

            {/* Removed Account Inbox button - using direct email replies instead */}
            
            <button 
              className="action-btn"
              onClick={() => {
                recordActivity('navigation', 'Opened Inbox', '🔔');
                onNavigateToApp('notifications');
              }}
            >
              <span className="btn-icon">🔔</span>
              <div>
                <span className="btn-title">Inbox</span>
                <span className="btn-subtitle">View messages & replies</span>
              </div>
            </button>
            <button 
              className="action-btn"
              onClick={() => {
                recordActivity('navigation', 'Opened Billing', '💳');
                onNavigateToApp('billing');
              }}
            >
              <span className="btn-icon">💳</span>
              <div>
                <span className="btn-title">Billing</span>
                <span className="btn-subtitle">Plan, usage & clip history</span>
              </div>
            </button>
            <button 
              className="action-btn"
              onClick={() => {
                recordActivity('feature_access', 'Opened email support form', '📧');
                setShowEmailSupport(true);
                setEsSubject('');
                setEsMessage('');
                setEsMsg('');
              }}
            >
              <span className="btn-icon">📧</span>
              <div>
                <span className="btn-title">Email Support</span>
                <span className="btn-subtitle">Send direct support email</span>
              </div>
            </button>
            {showEmailSupport && (
              <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999,padding:'20px'}} onClick={e=>{if(e.target===e.currentTarget)setShowEmailSupport(false)}}>
                <div style={{background:'linear-gradient(135deg,#1a1a2e,#16213e)',border:'1px solid rgba(139,92,246,0.3)',borderRadius:'16px',padding:'32px',maxWidth:'500px',width:'100%',maxHeight:'80vh',overflowY:'auto'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'24px'}}>
                    <h3 style={{color:'#e2e8f0',margin:0,fontSize:'20px',fontWeight:'700'}}>📧 Email Support</h3>
                    <button onClick={()=>setShowEmailSupport(false)} style={{background:'none',border:'none',color:'#94a3b8',fontSize:'24px',cursor:'pointer',padding:'4px'}}>✕</button>
                  </div>
                  <div style={{background:'rgba(139,92,246,0.1)',border:'1px solid rgba(139,92,246,0.2)',borderRadius:'10px',padding:'14px',marginBottom:'20px'}}>
                    <p style={{color:'#a78bfa',fontSize:'13px',margin:0}}>Sending as <strong>{user.username || user.email}</strong> ({user.plan || 'Free'} Plan)</p>
                    <p style={{color:'#64748b',fontSize:'12px',margin:'4px 0 0'}}>Reply will be sent to: {user.email}</p>
                  </div>
                  <label style={{color:'#94a3b8',fontSize:'13px',fontWeight:'600',display:'block',marginBottom:'6px'}}>Subject</label>
                  <input value={esSubject} onChange={e=>setEsSubject(e.target.value)} placeholder="Brief description of your issue" style={{width:'100%',padding:'12px',borderRadius:'8px',border:'1px solid rgba(255,255,255,0.15)',background:'rgba(255,255,255,0.05)',color:'#e2e8f0',fontSize:'14px',marginBottom:'16px',boxSizing:'border-box'}} />
                  <label style={{color:'#94a3b8',fontSize:'13px',fontWeight:'600',display:'block',marginBottom:'6px'}}>Message</label>
                  <textarea value={esMessage} onChange={e=>setEsMessage(e.target.value)} placeholder="Describe your issue or question in detail..." rows={6} style={{width:'100%',padding:'12px',borderRadius:'8px',border:'1px solid rgba(255,255,255,0.15)',background:'rgba(255,255,255,0.05)',color:'#e2e8f0',fontSize:'14px',marginBottom:'20px',boxSizing:'border-box',resize:'vertical',fontFamily:'inherit'}} />
                  <button 
                    onClick={async()=>{
                      if(!esSubject.trim()||!esMessage.trim()){setEsMsg('Please fill in both subject and message');return;}
                      setEsLoading(true);setEsMsg('');
                      try{
                        const ticketId='ES-'+Date.now().toString(36).toUpperCase();
                        const r=await fetch('/api/send-support-email',{
                          method:'POST',
                          headers:{'Content-Type':'application/json','Authorization':'Bearer '+localStorage.getItem('auth_token')},
                          body:JSON.stringify({
                            ticketId,
                            userEmail:user.email,
                            replyToEmail:user.email,
                            firstName:user.username||'User',
                            lastName:'',
                            userName:user.username||user.email?.split('@')[0],
                            userPlan:user.plan||'Free',
                            category:'Email Support',
                            priority:'Normal',
                            subject:esSubject,
                            message:esMessage,
                            device:navigator.userAgent.split(')')[0]+')'
                          })
                        });
                        const d=await r.json();
                        if(r.ok){setEsMsg('✅ Email sent! Check your inbox for confirmation.');setTimeout(()=>setShowEmailSupport(false),3000);}
                        else setEsMsg('❌ '+(d.error||'Failed to send'));
                      }catch(e){setEsMsg('❌ Network error');}
                      setEsLoading(false);
                    }}
                    disabled={esLoading||!esSubject.trim()||!esMessage.trim()}
                    style={{width:'100%',padding:'14px',borderRadius:'10px',border:'none',background:esSubject.trim()&&esMessage.trim()&&!esLoading?'linear-gradient(135deg,#8b5cf6,#7c3aed)':'rgba(255,255,255,0.1)',color:esSubject.trim()&&esMessage.trim()&&!esLoading?'white':'#64748b',fontWeight:'700',cursor:esSubject.trim()&&esMessage.trim()&&!esLoading?'pointer':'default',fontSize:'15px',transition:'all 0.3s'}}
                  >{esLoading?'Sending...':'Send Support Email'}</button>
                  {esMsg&&<p style={{color:esMsg.startsWith('✅')?'#34d399':'#f87171',fontSize:'13px',marginTop:'12px',textAlign:'center',fontWeight:'500'}}>{esMsg}</p>}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Usage Statistics */}
        {analytics && (
          <div className="dashboard-card usage-stats">
            <h2>📊 Usage Analytics</h2>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-number">{(analytics?.videosGenerated || 0) || 0}</span>
                <span className="stat-label">Total Videos</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{(analytics?.monthlyUsage || 0) || 0}</span>
                <span className="stat-label">This Month</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{(analytics?.totalClipsCreated || 0) || 0}</span>
                <span className="stat-label">Clips Created</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">
                  {analytics?.memberSince ? 
                    Math.floor((new Date() - new Date(analytics?.memberSince)) / (1000 * 60 * 60 * 24)) : 0
                  }
                </span>
                <span className="stat-label">Days Active</span>
              </div>
            </div>
          </div>
        )}

        {/* Clip Gallery */}
        <div className="dashboard-card" style={{background:'linear-gradient(135deg,rgba(25,25,50,0.95),rgba(40,37,90,0.95))'}}>
          <h2 id="dash-clips" style={{display:'flex',alignItems:'center',gap:'8px'}}>🎬 Recent Clips</h2>
          <ClipGallery />
        </div>
        {/* Usage Analytics */}
        <div className="dashboard-card" style={{background:'linear-gradient(135deg,rgba(25,25,50,0.95),rgba(40,37,90,0.95))'}}>
          <h2 id="dash-analytics" style={{display:'flex',alignItems:'center',gap:'8px'}}>📊 Usage Analytics</h2>
          <UsageAnalytics analytics={analytics} />
        </div>
        {/* Referral Program */}
        <div className="dashboard-card" style={{background:'linear-gradient(135deg,rgba(25,25,50,0.95),rgba(40,37,90,0.95))'}}>
          <h2 style={{display:'flex',alignItems:'center',gap:'8px'}}>🎁 Refer & Earn</h2>
          <ReferralPanel plan={user.plan} />
        </div>
        {/* AI Insights Card */}
        {aiInsights && (
          <div className="dashboard-card ai-insights-card">
            <h2>🤖 AI Insights</h2>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '12px'
              }}>
                <span style={{ color: '#e2e8f0', fontWeight: '600' }}>Viral Potential</span>
                <span style={{ 
                  color: '#10b981', 
                  fontWeight: '700',
                  fontSize: '18px'
                }}>
                  {aiInsights.viralPotential}%
                </span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${aiInsights.viralPotential}%` }}
                ></div>
              </div>
            </div>
            <div style={{ space: '16px' }}>
              <p style={{ color: '#cbd5e1', marginBottom: '12px' }}>
                💡 <strong>Optimization:</strong> {aiInsights.optimization}
              </p>
              <p style={{ color: '#cbd5e1', marginBottom: '12px' }}>
                🎯 <strong>Recommendation:</strong> {aiInsights.recommendation}
              </p>
              <p style={{ color: '#cbd5e1', marginBottom: '16px' }}>
                ⚡ <strong>Next Best Action:</strong> {aiInsights.nextBestAction}
              </p>
              <button 
                className="action-btn primary"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => onNavigateToApp('ai-insights')}
              >
                <span className="btn-icon">🤖</span>
                <span className="btn-title">Act on Insight</span>
              </button>
            </div>
          </div>
        )}

        {/* System Performance Card */}
        <div className="dashboard-card performance-card">
          <h2>⚡ System Performance</h2>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-number">{systemStatus.cpu}%</span>
              <span className="stat-label">CPU Usage</span>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ 
                    width: `${systemStatus.cpu}%`,
                    background: systemStatus.cpu > 80 ? 'linear-gradient(90deg, #ef4444, #dc2626)' :
                               systemStatus.cpu > 60 ? 'linear-gradient(90deg, #f59e0b, #d97706)' :
                               'linear-gradient(90deg, #10b981, #059669)'
                  }}
                ></div>
              </div>
            </div>
            <div className="stat-item">
              <span className="stat-number">{systemStatus.memory}%</span>
              <span className="stat-label">Memory</span>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ 
                    width: `${systemStatus.memory}%`,
                    background: systemStatus.memory > 80 ? 'linear-gradient(90deg, #ef4444, #dc2626)' :
                               systemStatus.memory > 60 ? 'linear-gradient(90deg, #f59e0b, #d97706)' :
                               'linear-gradient(90deg, #10b981, #059669)'
                  }}
                ></div>
              </div>
            </div>
            <div className="stat-item">
              <span className="stat-number">{systemStatus.queue}</span>
              <span className="stat-label">Queue Length</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{systemStatus.uptime}</span>
              <span className="stat-label">Uptime</span>
            </div>
          </div>
          
          <div style={{ 
            marginTop: '16px',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px'
          }}>
            <div style={{ 
              padding: '12px', 
              background: 'rgba(245, 158, 11, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(245, 158, 11, 0.2)'
            }}>
              <span style={{ color: '#f59e0b', fontWeight: '600', fontSize: '14px' }}>
                📍 Region: {systemStatus.region}
              </span>
            </div>
            
            <div style={{ 
              padding: '12px', 
              background: 'rgba(99, 102, 241, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(99, 102, 241, 0.2)'
            }}>
              <span style={{ color: '#6366f1', fontWeight: '600', fontSize: '14px' }}>
                🌐 Connection: {systemStatus.connection || 'Unknown'}
              </span>
            </div>
          </div>
          
          {systemStatus.latency && systemStatus.latency !== 'unknown' && (
            <div style={{ 
              marginTop: '12px',
              padding: '12px', 
              background: 'rgba(16, 185, 129, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(16, 185, 129, 0.2)',
              textAlign: 'center'
            }}>
              <span style={{ color: '#10b981', fontWeight: '600', fontSize: '14px' }}>
                ⚡ Latency: {systemStatus.latency}
              </span>
            </div>
          )}
        </div>

        {/* Live Users - Admin Only */}
        {user.isAdmin && (
          <div className="dashboard-card" style={{background:'linear-gradient(135deg,rgba(25,25,50,0.95),rgba(40,37,90,0.95))'}}>
            <h2 style={{display:'flex',alignItems:'center',gap:'8px'}}>👥 Live Users</h2>
            <AdminLiveUsers />
          </div>
        )}
        {/* Cookie Health - Admin Only */}
        {user.isAdmin && cookieHealth && (
          <div className="dashboard-card" style={{background: cookieHealth.health === 'critical' ? 'rgba(239,68,68,0.1)' : cookieHealth.health === 'warning' ? 'rgba(245,158,11,0.1)' : 'rgba(16,185,129,0.05)'}}>
            <h2>🍪 Cookie Health</h2>
            <div style={{display:'flex', alignItems:'center', gap:'16px', marginBottom:'16px'}}>
              <div style={{
                width:'80px', height:'80px', borderRadius:'50%',
                background: cookieHealth.healthScore > 70 ? 'conic-gradient(#10b981 ' + (cookieHealth.healthScore * 3.6) + 'deg, rgba(255,255,255,0.1) 0deg)' :
                  cookieHealth.healthScore > 30 ? 'conic-gradient(#f59e0b ' + (cookieHealth.healthScore * 3.6) + 'deg, rgba(255,255,255,0.1) 0deg)' :
                  'conic-gradient(#ef4444 ' + (cookieHealth.healthScore * 3.6) + 'deg, rgba(255,255,255,0.1) 0deg)',
                display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0
              }}>
                <div style={{width:'60px',height:'60px',borderRadius:'50%',background:'#1e1b4b',display:'flex',alignItems:'center',justifyContent:'center'}}>
                  <span style={{color:'white',fontWeight:'700',fontSize:'18px'}}>{cookieHealth.healthScore}%</span>
                </div>
              </div>
              <div>
                <div style={{color: cookieHealth.health === 'healthy' ? '#10b981' : cookieHealth.health === 'warning' ? '#f59e0b' : '#ef4444', fontWeight:'700', fontSize:'16px', textTransform:'uppercase', marginBottom:'4px'}}>
                  {cookieHealth.health === 'healthy' ? '✅ Healthy' : cookieHealth.health === 'aging' ? '⏳ Aging' : cookieHealth.health === 'warning' ? '⚠️ Warning' : '❌ Critical'}
                </div>
                <div style={{color:'#94a3b8',fontSize:'13px'}}>{cookieHealth.totalCookies} cookies • {cookieHealth.criticalPresent ? 'All critical present' : '⚠️ Missing: ' + (cookieHealth.missingCritical || []).join(', ')}</div>
                <div style={{color:'#64748b',fontSize:'12px',marginTop:'4px'}}>Last refreshed: {cookieHealth.daysSinceRefresh === 0 ? 'Today' : cookieHealth.daysSinceRefresh + ' days ago'}</div>
              </div>
            </div>
            {cookieHealth.daysSinceRefresh > 14 && (
              <div style={{padding:'10px 14px',background:'rgba(239,68,68,0.15)',borderRadius:'8px',color:'#fca5a5',fontSize:'13px',fontWeight:'600'}}>
                🔄 Cookies are {cookieHealth.daysSinceRefresh} days old. Refresh soon to avoid download failures.
              </div>
            )}
            <details style={{marginTop:'12px',cursor:'pointer'}}>
              <summary style={{color:'#94a3b8',fontSize:'13px',fontWeight:'600',padding:'8px 0'}}>📋 How to refresh cookies</summary>
              <div style={{padding:'12px',background:'rgba(99,102,241,0.08)',borderRadius:'8px',marginTop:'8px',fontSize:'12px',color:'#cbd5e1',lineHeight:'1.8'}}>
                <div><strong>1.</strong> Open Chrome and go to <span style={{color:'#818cf8'}}>youtube.com</span> (make sure you're signed in)</div>
                <div><strong>2.</strong> Install the <span style={{color:'#818cf8'}}>"Get cookies.txt LOCALLY"</span> Chrome extension</div>
                <div><strong>3.</strong> Click the extension icon on youtube.com and export cookies</div>
                <div><strong>4.</strong> Copy the full cookie file contents</div>
                <div><strong>5.</strong> SSH into server: <code style={{background:'rgba(0,0,0,0.3)',padding:'2px 6px',borderRadius:'4px',fontSize:'11px'}}>ssh root@vlogclipai</code></div>
                <div><strong>6.</strong> Paste into Claude chat to update server cookies file</div>
                <div style={{paddingLeft:'20px',color:'#94a3b8'}}>[paste cookie contents here]</div>
                <div style={{paddingLeft:'20px',color:'#94a3b8'}}>COOKIES</div>
                <div><strong>7.</strong> Restart: <code style={{background:'rgba(0,0,0,0.3)',padding:'2px 6px',borderRadius:'4px',fontSize:'11px'}}>pm2 restart all</code></div>
              </div>
            </details>
          </div>
        )}
        {/* Admin Review Management */}
        {user.isAdmin && (
          <div className="dashboard-card">
            <AdminReviewsPanel />
            <AdminUsersPanel />
          </div>
        )}

        {/* Live Reviews Management - Admin Only */}
        {user.isAdmin && (
          <div className="dashboard-card" style={{background:'linear-gradient(135deg,rgba(25,25,50,0.95),rgba(40,37,90,0.95))'}}>
            <h2 style={{display:'flex',alignItems:'center',gap:'8px'}}>💬 Live Reviews Management</h2>
            <AdminLiveReviews />
          </div>
        )}
        {/* Rate Your Experience (non-admin users) */}
        {!user.isAdmin && (
          <div className="dashboard-card">
            <FeedbackPanel />
          </div>
        )}

        {/* Enhanced Trending Content */}
        {trendingContent.length > 0 && (
          <div className="dashboard-card trending-card">
            <h2>🔥 Real-Time Trending Content</h2>
            <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '16px' }}>
              Updated every login • Data from multiple platforms
            </div>
            <div style={{ space: '12px' }}>
              {trendingContent.map((trend, index) => (
                <div key={index} style={{
                  padding: '14px 12px',
                  borderRadius: '10px',
                  background: 'rgba(255, 255, 255, 0.02)',
                  marginBottom: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.05)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                    <span style={{ color: '#ffffff', fontWeight: '600', fontSize: '14px' }}>{trend.topic}</span>
                    <span style={{
                      padding: '2px 8px',
                      background: trend.momentum === 'High' ? 'rgba(239,68,68,0.2)' : trend.momentum === 'Medium' ? 'rgba(245,158,11,0.2)' : 'rgba(16,185,129,0.2)',
                      border: '1px solid ' + (trend.momentum === 'High' ? 'rgba(239,68,68,0.4)' : trend.momentum === 'Medium' ? 'rgba(245,158,11,0.4)' : 'rgba(16,185,129,0.4)'),
                      borderRadius: '10px',
                      color: trend.momentum === 'High' ? '#ef4444' : trend.momentum === 'Medium' ? '#f59e0b' : '#10b981',
                      fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', whiteSpace: 'nowrap'
                    }}>{trend.momentum}</span>
                    <span style={{ marginLeft: 'auto', color: '#10b981', fontWeight: '700', fontSize: '14px', whiteSpace: 'nowrap' }}>{trend.growth}</span>
                  </div>
                  <div style={{ color: '#94a3b8', fontSize: '12px', lineHeight: '1.5' }}>
                    {trend.engagement?.toLocaleString()} engagements • {trend.platforms?.join(', ') || 'Multi-platform'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Activity */}
        <div className="dashboard-card recent-activity">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>🕒 Recent Activity</h2>
            {recentActivity.length > 0 && (
              <button 
                onClick={() => {
                  const userId = user.email || user.userId || 'default';
                  localStorage.removeItem(`activity_${userId}`);
                  localStorage.setItem(`activity_cleared_${userId}`, new Date().toISOString());
                  setRecentActivity([]);
                }}
                style={{
                  background: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#ef4444',
                  padding: '6px 12px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => { e.target.style.background = 'rgba(239, 68, 68, 0.3)'; }}
                onMouseOut={(e) => { e.target.style.background = 'rgba(239, 68, 68, 0.15)'; }}
              >
                🗑️ Clear All
              </button>
            )}
          </div>
          <div className="activity-list">
            {recentActivity.map((activity, index) => (
              <div key={index} className="activity-item">
                <span className="activity-icon">{activity.icon}</span>
                <div className="activity-content">
                  <span className="activity-title">{activity.title}</span>
                  <span className="activity-time">
                    {formatTimeAgo(activity.time)}
                  </span>
                </div>
              </div>
            ))}
            
            {analytics?.memberSince && (
              <div className="activity-item">
                <span className="activity-icon">🎉</span>
                <div className="activity-content">
                  <span className="activity-title">Joined VlogClip AI</span>
                  <span className="activity-time">
                    {new Date(analytics?.memberSince).toLocaleDateString()}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Enhanced Smart Notifications */}
        {notifications.length > 0 && (
          <div className="dashboard-card">
            <h2 id="dash-settings">🔔 Smart Notifications</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {notifications.map((notification) => {
                const getNotificationStyle = (type) => {
                  switch (type) {
                    case 'success': return { bg: 'rgba(16, 185, 129, 0.1)', border: 'rgba(16, 185, 129, 0.2)', icon: '✅' };
                    case 'info': return { bg: 'rgba(59, 130, 246, 0.1)', border: 'rgba(59, 130, 246, 0.2)', icon: 'ℹ️' };
                    case 'tip': return { bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 0.2)', icon: '💡' };
                    case 'message': return { bg: 'rgba(139, 92, 246, 0.1)', border: 'rgba(139, 92, 246, 0.2)', icon: '📧' };
                    case 'ai_insight': return { bg: 'rgba(236, 72, 153, 0.1)', border: 'rgba(236, 72, 153, 0.2)', icon: '🤖' };
                    default: return { bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 0.2)', icon: '🔔' };
                  }
                };
                
                const style = getNotificationStyle(notification.type);
                
                return (
                  <div 
                    key={notification.id} 
                    style={{
                      padding: '16px',
                      background: style.bg,
                      border: `1px solid ${style.border}`,
                      borderRadius: '12px',
                      marginBottom: '12px',
                      cursor: notification.actionable ? 'pointer' : 'default',
                      transition: 'all 0.2s ease',
                      position: 'relative'
                    }}
                    onClick={() => { if (notification.actionable) { if (notification.target) { onNavigateToApp(notification.target); } else if (notification.action) { notification.action(); } } }}
                    onMouseOver={(e) => {
                      if (notification.actionable) {
                        e.currentTarget.style.transform = 'translateX(4px)';
                        e.currentTarget.style.boxShadow = `0 4px 12px ${style.border}`;
                      }
                    }}
                    onMouseOut={(e) => {
                      if (notification.actionable) {
                        e.currentTarget.style.transform = 'translateX(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}
                  >
                    {notification.actionable && (
                      <div style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        color: '#94a3b8',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        Click to view →
                      </div>
                    )}
                    <div style={{ color: '#ffffff', fontWeight: '600', marginBottom: '4px', paddingRight: notification.actionable ? '80px' : '0' }}>
                      {style.icon} {notification.message}
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: '13px' }}>
                      {notification.time}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Legal Links - Dashboard Footer */}
      <div style={{
        textAlign: "center",
        padding: "24px 16px",
        marginTop: "32px",
        borderTop: "1px solid rgba(255,255,255,0.08)",
        position: "relative",
        zIndex: 1
      }}>
        <p style={{ color: "#64748b", fontSize: "13px", marginBottom: "12px" }}>
          © 2026 VlogClip AI - Powered by OpenAI & FFmpeg
        </p>
        <div style={{ display: "flex", justifyContent: "center", gap: "8px", flexWrap: "wrap" }}>
          <span onClick={() => onNavigateToApp("terms")} style={{ color: "#94a3b8", fontSize: "13px", cursor: "pointer", textDecoration: "underline", padding: "4px 8px" }}>Terms of Service</span>
          <span style={{ color: "#475569", fontSize: "13px" }}>|</span>
          <span onClick={() => onNavigateToApp("privacy")} style={{ color: "#94a3b8", fontSize: "13px", cursor: "pointer", textDecoration: "underline", padding: "4px 8px" }}>Privacy Policy</span>
        </div>
      </div>
      </div>
    </div>
  );
};

export default UserDashboard;